import React, { useEffect, useState } from 'react';
import OutlinedButton from '@/components/common/buttons/OutlinedButton';
import PrimaryButton from '@/components/common/buttons/PrimaryButton';
import {
	Accordion,
	Box,
	Circle,
	Heading,
	HStack,
	Icon,
	Input,
	Stack,
	Text,
	useBreakpointValue,
	VStack,
} from '@chakra-ui/react';
import { LuCheck } from 'react-icons/lu';
import { Field } from '@/components/ui/field';
import { useCurrentHouse } from '@/context/CurrentHouseContext';
import { useAuth } from '@/context/AuthContext';
import { usePropertyRoles } from '@/hooks/usePropertyRoles';
import AuthPrimaryButton from '@/components/authentication/ui/AuthPrimaryButton.tsx';
import { TRANSFER_EXPIRES_DAYS, useTransferOwnership } from '@/hooks/settings/useTransferOwnership';
import TransferSuccessComponent from '@/components/settings/components/TransferSuccessComponent';
import { EmailPattern } from '@/utils/emailUtil';
import { PrimaryCheckbox } from '@/components/ui/checkbox.tsx';
import PageContent from '@/components/layout/PageContent.tsx';
import { useProSubscription } from '@/hooks/payment/useProSubscription';
import SavedCardPaymentConfirmationDialog from '@/components/payment/SavedCardPaymentConfirmationDialog';
import { TRANSFER_STEP_TRANSITION_CSS } from '@/components/ui/motion';
import { splitAddress } from '@/utils/properties/addressUtils';
import { usePageScrollContainerRef } from '@/context/PageScrollContainerContext';
import { propertyNavigateOptions, PropertyRouteTo } from '@/routes';
import { useNavigate } from '@tanstack/react-router';

/** Ordered steps of the transfer flow. */
const TransferStep = {
	Checklist: 'checklist',
	ProfessionalCopy: 'professional-copy',
	Details: 'details',
	Review: 'review',
} as const;
type TransferStep = (typeof TransferStep)[keyof typeof TransferStep];
export const TRANSFER_RETURN_TARGETS = ['settings', 'settingsAccess'] as const;
export type TransferReturnTarget = (typeof TRANSFER_RETURN_TARGETS)[number];

/** Pre-transfer confirmations the owner must tick before continuing. */
const READINESS_ITEMS = [
	{
		title: 'Property information is up to date',
		description: 'Key details and records are current and ready for the next owner.',
	},
	// {
	// 	title: 'Important documents are included',
	// 	description: 'Any documents the new owner should receive are attached to the property.',
	// },
	// {
	// 	title: 'Outstanding items are clear',
	// 	description: 'Anything unresolved has been completed, or clearly noted for the new owner.',
	// },
	{
		title: 'Anything that should not transfer has been removed',
		description:
			'Review the property for notes, files, or details that should not be handed over to the new owner.',
	},
];

const TRANSFER_COPY = {
	readinessHeading: 'Before you transfer',
	readinessIntro:
		"When the new owner accepts, this Bundle passes to them. Take a moment to confirm it's ready to hand over.",
	detailsHelp: `We'll send the transfer invitation to this email address. The new owner has ${TRANSFER_EXPIRES_DAYS} days to claim ownership from you.`,
	professionalCopyHelp:
		"A professional's copy is an archival snapshot made when the new owner accepts the transfer. It does not stay synced automatically, but any uploads to your archive copy get offered as updates to the new owner's live copy.",
	reviewIntro: 'You are transferring ownership of',
	reviewAcknowledgement: 'I understand once the new owner claims, they control the property.',
	addressConfirmationLabel: 'Type the property address to confirm',
	pageHeading: 'Transfer ownership',
	professionalCopyQuestion: "Do you need a professional's copy?",
	professionalCopyHelpTrigger: "What is a professional's copy?",
	newOwnerHeading: 'New owner',
	continueAction: 'Continue',
	subscribeAction: 'Subscribe',
	backAction: 'Back',
	cancelAction: 'Cancel',
	sendAction: 'Send',
	openingCheckoutLoading: 'Opening checkout...',
	sendingLoading: 'Sending...',
	copyAttestation: 'I confirm I am keeping this copy for professional or statutory record-keeping purposes.',
	emailOwnError: 'You already own this property. Enter a different email address to transfer ownership.',
	emailMismatchError: 'Emails do not match',
} as const;

/** Steps shown in the progress indicator. */
const CREATOR_STEP_SEQUENCE: TransferStep[] = [
	TransferStep.Checklist,
	TransferStep.ProfessionalCopy,
	TransferStep.Details,
	TransferStep.Review,
];
const STANDARD_STEP_SEQUENCE: TransferStep[] = [TransferStep.Checklist, TransferStep.Details, TransferStep.Review];

/** Whether to keep a separate professional copy of the records after the living record transfers. */
const ProfessionalCopy = { None: 'none', Keep: 'keep' } as const;
const PROFESSIONAL_COPY_OPTIONS = [
	{ value: ProfessionalCopy.None, label: 'No copy needed', description: 'Transfer the living record only.' },
	{
		value: ProfessionalCopy.Keep,
		label: "Keep a professional's copy",
		description: 'Requires a Bundle Pro subscription.',
	},
];

const PAGE_HEADING_PROPS = {
	fontSize: '24px',
	lineHeight: '30px',
	fontWeight: '600',
	color: 'brand.fg',
	letterSpacing: '-0.01em',
} as const;

const STEP_HEADING_PROPS = {
	fontSize: '20px',
	lineHeight: '26px',
	fontWeight: '600',
	color: 'brand.fg',
} as const;

const PRIMARY_ACTION_PROPS = {
	fontSize: 'sm',
	fontWeight: 'medium',
} as const;

const ACTION_GROUP_PROPS = {
	direction: { base: 'column', md: 'row-reverse' },
	gap: { base: 3, md: 4 },
	w: '100%',
	align: { base: 'stretch', md: 'center' },
} as const;

const PRIMARY_ACTION_BUTTON_PROPS = {
	w: { base: '100%', md: 'auto' },
	minW: { md: '128px' },
	h: { base: '48px', md: '40px' },
	pl: 5,
	pr: 5,
} as const;

const SECONDARY_ACTION_BUTTON_PROPS = {
	w: { base: '100%', md: 'auto' },
	minW: { md: '96px' },
	h: { base: '48px', md: '40px' },
	px: 5,
} as const;

const normalizeConfirmationText = (value: string) => value.trim().replace(/\s+/g, ' ').toLowerCase();

interface UseTransferOwnershipFlowProps {
	isCreator: boolean;
	isSubscribed: boolean;
	onOpenSubscription: () => void;
	propertyAddress: string;
}

interface TransferOwnershipSubmitOptions {
	detailsReady: boolean;
	onTransferSubmit: () => void;
}

const useTransferOwnershipFlow = ({
	isCreator,
	isSubscribed,
	onOpenSubscription,
	propertyAddress,
}: UseTransferOwnershipFlowProps) => {
	const [step, setStep] = useState<TransferStep>(TransferStep.Checklist);
	const [readiness, setReadiness] = useState<boolean[]>(() => READINESS_ITEMS.map(() => false));
	const [copyChoice, setCopyChoice] = useState<string>(ProfessionalCopy.None);
	const [copyAttested, setCopyAttested] = useState(false);
	const [understood, setUnderstood] = useState(false);
	const [addressConfirmation, setAddressConfirmation] = useState('');

	const keepProfessionalCopy = copyChoice === ProfessionalCopy.Keep;
	const allReady = readiness.every(Boolean);
	const copyReady = !keepProfessionalCopy || copyAttested;
	const copyPrimaryActionLabel =
		keepProfessionalCopy && !isSubscribed ? TRANSFER_COPY.subscribeAction : TRANSFER_COPY.continueAction;
	const stepSequence = isCreator ? CREATOR_STEP_SEQUENCE : STANDARD_STEP_SEQUENCE;
	const stepIndex = stepSequence.indexOf(step);
	const addressConfirmationText = splitAddress(propertyAddress).street;
	const addressConfirmed =
		normalizeConfirmationText(addressConfirmation) === normalizeConfirmationText(addressConfirmationText);
	const reviewReady = understood && addressConfirmed;

	const toggleReadiness = (index: number) =>
		setReadiness((prev) => prev.map((value, i) => (i === index ? !value : value)));

	const handleFormSubmit =
		({ detailsReady, onTransferSubmit }: TransferOwnershipSubmitOptions) =>
		(e: React.FormEvent) => {
			e.preventDefault();

			switch (step) {
				case TransferStep.Checklist:
					if (allReady) setStep(isCreator ? TransferStep.ProfessionalCopy : TransferStep.Details);
					return;
				case TransferStep.ProfessionalCopy:
					if (!copyReady) return;
					if (keepProfessionalCopy && !isSubscribed) {
						onOpenSubscription();
						return;
					}
					setStep(TransferStep.Details);
					return;
				case TransferStep.Details:
					if (detailsReady) setStep(TransferStep.Review);
					return;
				case TransferStep.Review:
					if (reviewReady) onTransferSubmit();
					return;
			}
		};

	return {
		step,
		setStep,
		readiness,
		toggleReadiness,
		allReady,
		copyChoice,
		setCopyChoice,
		copyAttested,
		setCopyAttested,
		keepProfessionalCopy,
		copyReady,
		copyPrimaryActionLabel,
		understood,
		setUnderstood,
		addressConfirmation,
		setAddressConfirmation,
		addressConfirmationText,
		reviewReady,
		stepSequence,
		stepIndex,
		handleFormSubmit,
	};
};

/**
 * Property ownership transfer flow. Creators decide whether to keep a professional copy before entering
 * new-owner details, then everyone reviews and confirms the transfer before the invitation is sent. The
 * property stays the owner's until the recipient accepts; the pending/cancel state lives in OwnershipSection.
 */
interface TransferOwnershipPageProps {
	returnTo?: TransferReturnTarget;
}

const TransferOwnershipPage: React.FC<TransferOwnershipPageProps> = ({ returnTo = 'settings' }) => {
	const { house } = useCurrentHouse();
	const { user } = useAuth();
	const { isCreator } = usePropertyRoles();
	const pageScrollRef = usePageScrollContainerRef();
	const navigate = useNavigate();

	const {
		isSubscribed,
		initiateCheckout,
		isConfirmationOpen,
		savedCard,
		pendingAmountCents,
		confirmCheckout,
		cancelCheckout,
		isCheckingOut,
	} = useProSubscription();

	const flow = useTransferOwnershipFlow({
		isCreator,
		isSubscribed,
		onOpenSubscription: () => void initiateCheckout(),
		propertyAddress: house.address,
	});

	const {
		register,
		handleSubmit,
		setFocus,
		watchedFirstName,
		watchedLastName,
		watchedEmail,
		watchedConfirmEmail,
		emailsMatch,
		showSuccess,
		transferMutation,
		canSubmit,
		onSubmit,
	} = useTransferOwnership({ houseId: house.id, isBuilder: flow.keepProfessionalCopy });

	// Avoid auto-popping the keyboard on mobile (matches HouseStep).
	const shouldAutoFocus = useBreakpointValue({ base: false, md: true }) ?? false;
	const shouldResetStepScroll = useBreakpointValue({ base: true, md: false }) ?? false;

	useEffect(() => {
		if (!shouldResetStepScroll) return;
		pageScrollRef?.current?.scrollTo({ top: 0, behavior: 'auto' });
	}, [flow.step, pageScrollRef, shouldResetStepScroll]);

	// Focus the first name field when the details step appears (desktop only). autoFocus is unreliable
	// here since useBreakpointValue resolves after mount, so focus imperatively.
	useEffect(() => {
		if (!shouldAutoFocus || flow.step !== TransferStep.Details) return;
		const frameId = requestAnimationFrame(() => setFocus('firstName'));
		return () => cancelAnimationFrame(frameId);
	}, [shouldAutoFocus, flow.step, setFocus]);

	if (showSuccess) {
		return <TransferSuccessComponent houseAddress={house.address} />;
	}

	// Guard: the new owner can't be the current owner.
	const ownEmail = user?.email?.trim().toLowerCase() ?? '';
	const emailIsOwnEmail = !!ownEmail && watchedEmail?.trim().toLowerCase() === ownEmail;
	const detailsReady = canSubmit && !emailIsOwnEmail;
	const handleFormSubmit = flow.handleFormSubmit({
		detailsReady,
		onTransferSubmit: () => handleSubmit((data) => onSubmit(data, flow.reviewReady))(),
	});
	const handleCancelTransfer = () => {
		const target = returnTo === 'settingsAccess' ? PropertyRouteTo.settingsAccess : PropertyRouteTo.settings;
		void navigate(propertyNavigateOptions(house.public_id, target, {}));
	};

	const ownerName = `${watchedFirstName?.trim() ?? ''} ${watchedLastName?.trim() ?? ''}`.trim();
	const summaryRows = [
		{ label: 'New owner', value: ownerName || '-' },
		{ label: 'Email', value: watchedEmail?.trim() || '-' },
		// Professional copy is a creator-only choice (its own step), so only summarise it for them.
		...(isCreator ? [{ label: "Professional's copy", value: flow.keepProfessionalCopy ? 'Yes' : 'No' }] : []),
		{ label: 'Invitation expiry', value: `${TRANSFER_EXPIRES_DAYS} days` },
	];

	return (
		<PageContent>
			<Box w="100%" maxW={{ base: '100%', md: '600px' }} mt={{ base: 6, md: 12 }}>
				<form onSubmit={handleFormSubmit}>
					<VStack gap={8} align="stretch">
						<HStack justify="space-between" align="center" gap={4}>
							<Heading {...PAGE_HEADING_PROPS}>{TRANSFER_COPY.pageHeading}</Heading>
							<HStack gap={1} flexShrink={0}>
								{flow.stepSequence.map((s, i) => (
									<Box
										key={s}
										h="3px"
										w="22px"
										borderRadius="full"
										bg={i <= flow.stepIndex ? 'brand.solid' : 'border.default'}
									/>
								))}
							</HStack>
						</HStack>

						{/* Step 1: Readiness checklist */}
						{flow.step === TransferStep.Checklist && (
							<Box key="checklist" css={TRANSFER_STEP_TRANSITION_CSS}>
								<VStack gap={8} align="stretch">
									<VStack gap={3} align="stretch">
										<VStack gap={1} align="stretch">
											<Heading {...STEP_HEADING_PROPS}>{TRANSFER_COPY.readinessHeading}</Heading>
											<Text color="fg.muted" fontSize="md" lineHeight="1.6">
												{TRANSFER_COPY.readinessIntro}
											</Text>
										</VStack>

										<VStack gap={2.5} align="stretch">
											{READINESS_ITEMS.map((item, index) => (
												<Box
													key={item.title}
													borderWidth="1px"
													borderColor="border.default"
													borderRadius="xl"
													bg={flow.readiness[index] ? 'surface.default' : 'surface.subtle'}
													px={4}
													py={3.5}
												>
													<PrimaryCheckbox
														checked={flow.readiness[index]}
														onCheckedChange={() => flow.toggleReadiness(index)}
														controlProps={{ borderRadius: 'md' }}
														alignItems="flex-start"
													>
														<VStack gap={0.5} align="start">
															<Text
																textStyle="body.strong"
																color="brand.fg"
																lineHeight="1.4"
															>
																{item.title}
															</Text>
															<Text fontSize="sm" color="fg.muted" lineHeight="1.5">
																{item.description}
															</Text>
														</VStack>
													</PrimaryCheckbox>
												</Box>
											))}
										</VStack>
									</VStack>

									<Stack {...ACTION_GROUP_PROPS}>
										<PrimaryButton
											py={2}
											{...PRIMARY_ACTION_PROPS}
											{...PRIMARY_ACTION_BUTTON_PROPS}
											type="submit"
											disabled={!flow.allReady}
										>
											{TRANSFER_COPY.continueAction}
										</PrimaryButton>
										<OutlinedButton
											elevated
											{...SECONDARY_ACTION_BUTTON_PROPS}
											type="button"
											onClick={handleCancelTransfer}
										>
											{TRANSFER_COPY.cancelAction}
										</OutlinedButton>
									</Stack>
								</VStack>
							</Box>
						)}

						{/* Step 2: Professional copy */}
						{flow.step === TransferStep.ProfessionalCopy && (
							<Box key="professional-copy" css={TRANSFER_STEP_TRANSITION_CSS}>
								<VStack gap={8} align="stretch">
									<VStack gap={6} align="stretch">
										<Heading {...STEP_HEADING_PROPS}>
											{TRANSFER_COPY.professionalCopyQuestion}
										</Heading>

										<Accordion.Root collapsible variant="plain">
											<Accordion.Item value="professional-copy-help" borderWidth="0">
												<Accordion.ItemTrigger
													px={0}
													py={0}
													cursor="pointer"
													_hover={{ bg: 'transparent' }}
												>
													<HStack justify="space-between" w="100%" gap={3}>
														<Text textStyle="label.lg" color="brand.fg">
															{TRANSFER_COPY.professionalCopyHelpTrigger}
														</Text>
														<Accordion.ItemIndicator color="fg.muted" />
													</HStack>
												</Accordion.ItemTrigger>
												<Accordion.ItemContent>
													<Accordion.ItemBody px={0} pt={3} pb={0}>
														<Text color="fg.muted" fontSize="md" lineHeight="1.6">
															{TRANSFER_COPY.professionalCopyHelp}
														</Text>
													</Accordion.ItemBody>
												</Accordion.ItemContent>
											</Accordion.Item>
										</Accordion.Root>

										<VStack
											gap={3}
											align="stretch"
											role="radiogroup"
											aria-label="Professional copy"
										>
											{PROFESSIONAL_COPY_OPTIONS.map((option) => {
												const selected = flow.copyChoice === option.value;
												return (
													<HStack
														key={option.value}
														role="radio"
														aria-checked={selected}
														gap={3}
														px={4}
														py={4}
														bg={selected ? 'surface.default' : 'surface.subtle'}
														borderRadius="2xl"
														borderWidth={selected ? '2px' : '1px'}
														borderColor={selected ? 'brand.fg' : 'border.default'}
														cursor="pointer"
														_hover={{ bg: 'surface.default' }}
														onClick={() => flow.setCopyChoice(option.value)}
													>
														<VStack flex={1} gap={0.5} align="start">
															<Text
																textStyle="label.lg"
																color="brand.fg"
																lineHeight="1.4"
															>
																{option.label}
															</Text>
															<Text fontSize="sm" color="fg.muted" lineHeight="1.4">
																{option.description}
															</Text>
														</VStack>
														<Circle
															size="20px"
															borderWidth="2px"
															borderColor={selected ? 'brand.fg' : 'border.default'}
															bg={selected ? 'brand.fg' : 'transparent'}
															flexShrink={0}
														>
															{selected && (
																<Icon as={LuCheck} boxSize={3} color="white" />
															)}
														</Circle>
													</HStack>
												);
											})}
										</VStack>

										{flow.keepProfessionalCopy && (
											<Box bg="surface.subtle" borderRadius="xl" p={4}>
												<PrimaryCheckbox
													checked={flow.copyAttested}
													onCheckedChange={(details) =>
														flow.setCopyAttested(details.checked as boolean)
													}
													controlProps={{ borderRadius: 'md' }}
													alignItems="flex-start"
												>
													<Text fontSize="md" color="brand.fg" lineHeight="1.6">
														{TRANSFER_COPY.copyAttestation}
													</Text>
												</PrimaryCheckbox>
											</Box>
										)}
									</VStack>

									<Stack {...ACTION_GROUP_PROPS}>
										<PrimaryButton
											py={2}
											{...PRIMARY_ACTION_PROPS}
											{...PRIMARY_ACTION_BUTTON_PROPS}
											type="submit"
											disabled={!flow.copyReady}
											loading={isCheckingOut}
											loadingText={TRANSFER_COPY.openingCheckoutLoading}
										>
											{flow.copyPrimaryActionLabel}
										</PrimaryButton>
										<OutlinedButton
											elevated
											type="button"
											{...SECONDARY_ACTION_BUTTON_PROPS}
											onClick={() => flow.setStep(TransferStep.Checklist)}
										>
											{TRANSFER_COPY.backAction}
										</OutlinedButton>
									</Stack>
								</VStack>
							</Box>
						)}

						{/* Step 3: New owner details */}
						{flow.step === TransferStep.Details && (
							<Box key="details" css={TRANSFER_STEP_TRANSITION_CSS}>
								<VStack gap={6} align="stretch">
									<VStack gap={4} w="100%" align="stretch">
										<Heading {...STEP_HEADING_PROPS}>{TRANSFER_COPY.newOwnerHeading}</Heading>
										<Stack
											direction={{ base: 'column', md: 'row' }}
											gap={4}
											align="flex-start"
											w="100%"
										>
											{/* Required/pattern rules stay (they gate the Continue button via isValid) but we
											    don't surface per-field errors. The only meaningful feedback is the email
											    mismatch below, since the button is blocked until everything is valid. The
											    labels make placeholders redundant, so they're omitted. */}
											<Field label="First name" flex={1} required>
												<Input {...register('firstName', { required: true })} />
											</Field>

											<Field label="Last name" flex={1} required>
												<Input {...register('lastName', { required: true })} />
											</Field>
										</Stack>

										<Field
											label="Email"
											required
											invalid={emailIsOwnEmail}
											errorText={emailIsOwnEmail ? TRANSFER_COPY.emailOwnError : undefined}
										>
											<Input
												{...register('email', { required: true, pattern: EmailPattern })}
												type="email"
											/>
										</Field>

										<Field
											label="Confirm email"
											required
											invalid={!!watchedConfirmEmail && !emailsMatch}
											errorText={
												!emailsMatch && watchedConfirmEmail
													? TRANSFER_COPY.emailMismatchError
													: undefined
											}
										>
											<Input {...register('confirmEmail', { required: true })} type="email" />
										</Field>

										<Text fontSize="sm" color="fg.muted" lineHeight="1.6">
											{TRANSFER_COPY.detailsHelp}
										</Text>
									</VStack>

									<Stack {...ACTION_GROUP_PROPS}>
										<PrimaryButton
											py={2}
											{...PRIMARY_ACTION_PROPS}
											{...PRIMARY_ACTION_BUTTON_PROPS}
											type="submit"
											disabled={!detailsReady}
										>
											{TRANSFER_COPY.continueAction}
										</PrimaryButton>
										<OutlinedButton
											elevated
											type="button"
											onClick={() =>
												flow.setStep(
													isCreator ? TransferStep.ProfessionalCopy : TransferStep.Checklist,
												)
											}
											{...SECONDARY_ACTION_BUTTON_PROPS}
										>
											{TRANSFER_COPY.backAction}
										</OutlinedButton>
									</Stack>
								</VStack>
							</Box>
						)}

						{/* Step 4: Review & confirm */}
						{flow.step === TransferStep.Review && (
							<Box key="review" w="full" css={TRANSFER_STEP_TRANSITION_CSS}>
								<VStack gap={10} w="full" align="stretch">
									<VStack gap={8} w="full" align="stretch">
										<VStack gap={5} w="full" align="stretch">
											<VStack gap={2} align="center" textAlign="center">
												<Text fontSize="md" color="fg.muted">
													{TRANSFER_COPY.reviewIntro}
												</Text>
												<Heading {...STEP_HEADING_PROPS} textAlign="center">
													{house.address}
												</Heading>
											</VStack>
											<Box>
												{summaryRows.map((row, index) => (
													<HStack
														key={row.label}
														justify="space-between"
														gap={5}
														py={3}
														borderBottomWidth={index === summaryRows.length - 1 ? 0 : '1px'}
														borderColor="border.default"
													>
														<Text color="fg.muted" fontSize="md">
															{row.label}
														</Text>
														<Text
															textStyle="label.lg"
															color="brand.fg"
															textAlign="right"
															wordBreak="break-word"
														>
															{row.value}
														</Text>
													</HStack>
												))}
											</Box>
										</VStack>

										<VStack gap={5} align="stretch">
											<Box bg="surface.subtle" borderRadius="xl" p={4}>
												<PrimaryCheckbox
													checked={flow.understood}
													onCheckedChange={(details) =>
														flow.setUnderstood(details.checked as boolean)
													}
													controlProps={{ borderRadius: 'md' }}
													alignItems="center"
												>
													<Text fontSize="md" color="brand.fg" lineHeight="1.45">
														{TRANSFER_COPY.reviewAcknowledgement}
													</Text>
												</PrimaryCheckbox>
											</Box>

											{/* TODO: real re-authentication (password / Google) before sending is deferred.
											    Typing the address is interim intent-confirmation, not identity assurance —
											    add an auth step here before this ships if security re-auth is required. */}
											<Field label={TRANSFER_COPY.addressConfirmationLabel} required>
												<Input
													value={flow.addressConfirmation}
													placeholder={flow.addressConfirmationText}
													onChange={(event) =>
														flow.setAddressConfirmation(event.target.value)
													}
												/>
											</Field>
										</VStack>
									</VStack>

									<Stack {...ACTION_GROUP_PROPS}>
										<AuthPrimaryButton
											{...PRIMARY_ACTION_PROPS}
											{...PRIMARY_ACTION_BUTTON_PROPS}
											type="submit"
											display={{ base: 'inline-flex', md: 'none' }}
											loading={transferMutation.isPending}
											loadingText={TRANSFER_COPY.sendingLoading}
											disabled={!flow.reviewReady}
										>
											{TRANSFER_COPY.sendAction}
										</AuthPrimaryButton>
										<PrimaryButton
											py={2}
											{...PRIMARY_ACTION_PROPS}
											{...PRIMARY_ACTION_BUTTON_PROPS}
											type="submit"
											display={{ base: 'none', md: 'inline-flex' }}
											loading={transferMutation.isPending}
											loadingText={TRANSFER_COPY.sendingLoading}
											disabled={!flow.reviewReady}
										>
											{TRANSFER_COPY.sendAction}
										</PrimaryButton>
										<OutlinedButton
											elevated
											type="button"
											{...SECONDARY_ACTION_BUTTON_PROPS}
											onClick={() => flow.setStep(TransferStep.Details)}
											disabled={transferMutation.isPending}
										>
											{TRANSFER_COPY.backAction}
										</OutlinedButton>
									</Stack>
								</VStack>
							</Box>
						)}
					</VStack>
				</form>
			</Box>
			{/* Saved-card confirmation for the Pro subscription that keeping a professional copy requires. */}
			<SavedCardPaymentConfirmationDialog
				title="Subscribe to Bundle Pro?"
				amountSuffix="per month"
				isOpen={isConfirmationOpen}
				amountCents={pendingAmountCents}
				card={savedCard}
				isLoading={isCheckingOut}
				onCancel={cancelCheckout}
				onConfirm={confirmCheckout}
			/>
		</PageContent>
	);
};

export default TransferOwnershipPage;
